import { ref, watch } from 'vue';
import { useAxios } from '@vueuse/integrations/useAxios';

import { isRequestCanceled, logError } from '@/api/ApiHelpers';
import { showErrorMessage } from '@/components/common/errorMessage';

import type { ApiPayload } from '@/api/ApiTypes';


type OtlPlot = {
	period: string;
	image: string | null;
	updated: boolean;
};

type OtlPlotsRequest = {
	id: number;
	period_days: number;
};

type OtlPlotsResponse = ApiPayload & {
	plot: OtlPlot | null;
	errors?: unknown;
};

const DEFAULT_RESPONSE: OtlPlotsResponse = { plot: null };


const useOtlPlotsGet = () => {
	const { data, isLoading, error, execute } = useAxios<OtlPlotsResponse>(
		'/base_station/get_otl_plots/',
		{ method: 'POST' },
		{ immediate: false },
	);
	const apiData = ref<OtlPlotsResponse>(DEFAULT_RESPONSE);
	const resetData = (): void => {
		apiData.value = DEFAULT_RESPONSE;
	};
	watch(data, response => {
		if (response == null) {
			resetData();
		} else if (response.status === 'ok') {
			apiData.value = response;
		} else {
			showErrorMessage(response.errors);
		}
	});
	watch(error, err => {
		if (err != null) {
			if (isRequestCanceled(err)) {
				console.warn('api request canceled', {
					code: typeof err === 'object' && err != null && 'code' in err ? err.code : undefined,
					name: typeof err === 'object' && err != null && 'name' in err ? err.name : undefined,
					message: typeof err === 'object' && err != null && 'message' in err ? err.message : undefined,
					method: typeof err === 'object' && err != null && 'config' in err
						? (err.config as { method?: unknown } | undefined)?.method
						: undefined,
					url: typeof err === 'object' && err != null && 'config' in err
						? (err.config as { url?: unknown } | undefined)?.url
						: undefined,
					visibility: typeof document === 'undefined' ? undefined : document.visibilityState,
				});
				return;
			}
			logError(err);
		}
	});
	const doRequest = (dataObject: OtlPlotsRequest): void => {
		execute({ data: dataObject });
	};
	return { apiData, isLoading, doRequest, resetData };
};


export default {
	useOtlPlotsGet,
};
